import { DateTime } from "luxon";
import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { TripleShift, shiftNameToLetter } from "../../../types/Shift";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { setShiftEditability } from "../../../state/reducers/shiftReducer";
import { settingsAjax } from "../../../ajax/SettingsAjax";
import { useGlobalModal } from "../../Contexts/GlobalModalContext";
import { ModalActions } from "../../Modals/ModalActions";
import { Button } from "../../Buttons/Button";
import { useSnackbar } from "notistack";

type ShiftLockStatusChangeModalProps = {
  isCurrentlyLocked: boolean
  selectedDate: DateTime;
  selectedShift: TripleShift;
};

const ShiftLockStatusChangeModal: React.FC<ShiftLockStatusChangeModalProps> = ({
  isCurrentlyLocked,
  selectedDate,
  selectedShift
}) => {
  const { hideModal } = useGlobalModal();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const handleConfirm = useCallback(async () => {
    const selectedShiftChar = shiftNameToLetter(selectedShift);
    let res;

    if (isCurrentlyLocked)
      res = await settingsAjax.unlockShift(selectedDate, selectedShiftChar);
    else
      res = await settingsAjax.relockShift(selectedDate, selectedShiftChar);

    if (!ErrorResponse.isErrorResponse(res) && res.data) {
      dispatch(setShiftEditability(res.data));
      hideModal();
    }
    else
      enqueueSnackbar("Failed to fetch shift editability", { variant: "error" });
  }, [dispatch, hideModal, isCurrentlyLocked, selectedDate, selectedShift, enqueueSnackbar])

  return (
    <>
      <h2 className="text-2xl font-semibold mb-4 text-theme-text">{isCurrentlyLocked ? "Unlock Shift?" : "Lock Shift?"}</h2>
      <p className="mb-4 text-theme-text">
        {isCurrentlyLocked
          ? "This will allow you and other users to edit data for this shift."
          : "This will prevent you and other users from editing data for this shift."}
      </p>
      <ModalActions className="!mt-12">
        <Button variant="muted" onClick={hideModal}>Cancel</Button>
        <Button
          variant="info"
          isDefaultAction
          mnemonic={isCurrentlyLocked ? "u" : "l"}
          onClick={handleConfirm}
        >
          {isCurrentlyLocked ? "Unlock" : "Lock"}
        </Button>
      </ModalActions>
    </>
  )
};

export default ShiftLockStatusChangeModal;